import React from 'react';
import { useTranslation } from 'react-i18next';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();

  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: January 8, 2025</p>

        <h2>1. Introduction</h2>
        <p>Welcome to YME PAD ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services (collectively, the "Service").</p>
        
        <p>YME PAD is a Yu-Gi-Oh! tournament management and card shop platform that allows users to participate in tournaments, purchase cards and accessories, and interact with the Yu-Gi-Oh! community.</p>

        <h2>2. Information We Collect</h2>
        
        <h3>2.1 Information You Provide Directly</h3>
        <ul>
          <li><strong>Account Information:</strong> Email address, username/pseudo, Facebook link (optional)</li>
          <li><strong>Profile Information:</strong> Profile pictures, banner images, Master Duel ID, tournament statistics</li>
          <li><strong>Tournament Data:</strong> Tournament registrations, match results, deck screenshots, chat messages with opponents</li>
          <li><strong>Purchase Information:</strong> Purchase history, DP (Duelist Points) transactions</li>
          <li><strong>Communication:</strong> Messages sent through our tournament chat system, support requests</li>
        </ul>

        <h3>2.2 Information Collected Automatically</h3>
        <ul>
          <li><strong>Device Information:</strong> Device type, operating system, app version</li>
          <li><strong>Usage Data:</strong> App interactions, features used, time spent in app</li>
          <li><strong>Technical Data:</strong> Browser type, crash reports, performance metrics</li>
          <li><strong>Session Data:</strong> Login sessions stored locally for app functionality</li>
        </ul>

        <h3>2.3 Third-Party Information</h3>
        <ul>
          <li><strong>Facebook/Messenger:</strong> If you choose to connect your Facebook account for tournament notifications</li>
          <li><strong>Payment Processors:</strong> Transaction information for DP purchases (we do not store payment card details)</li>
        </ul>

        <h2>3. How We Use Your Information</h2>
        <p>We use the collected information for the following purposes:</p>
        
        <h3>3.1 Service Provision</h3>
        <ul>
          <li>Create and manage your account</li>
          <li>Process tournament registrations and match results</li>
          <li>Handle purchases and order fulfillment</li>
          <li>Provide customer support</li>
          <li>Enable chat and communication features</li>
        </ul>

        <h3>3.2 Service Improvement</h3>
        <ul>
          <li>Analyze app usage and performance</li>
          <li>Develop new features and improvements</li>
          <li>Fix bugs and technical issues</li>
          <li>Conduct research and analytics</li>
        </ul>

        <h3>3.3 Communication</h3>
        <ul>
          <li>Send tournament notifications and updates</li>
          <li>Provide order confirmations and shipping updates</li>
          <li>Send important service announcements</li>
          <li>Respond to your inquiries and support requests</li>
        </ul>

        <h3>3.4 Legal and Safety</h3>
        <ul>
          <li>Comply with legal obligations</li>
          <li>Protect against fraud and abuse</li>
          <li>Enforce our Terms of Service</li>
          <li>Protect the rights and safety of our users</li>
        </ul>

        <h2>4. Information Sharing and Disclosure</h2>
        
        <h3>4.1 We Do NOT Sell Your Personal Information</h3>
        <p>We do not sell, trade, or rent your personal information to third parties.</p>

        <h3>4.2 Limited Sharing</h3>
        <p>We may share your information in the following limited circumstances:</p>
        <ul>
          <li><strong>Service Providers:</strong> With trusted third-party services that help us operate our app (Firebase, payment processors, hosting services)</li>
          <li><strong>Tournament Participants:</strong> Your username and match results may be visible to other tournament participants</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
          <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</li>
        </ul>

        <h3>4.3 Public Information</h3>
        <p>The following information may be publicly visible to other users:</p>
        <ul>
          <li>Your username/pseudo</li>
          <li>Tournament rankings and statistics</li>
          <li>Profile pictures and banner images</li>
          <li>Tournament participation and match results</li>
          <li>Leaderboard positions</li>
        </ul>

        <h2>5. Data Storage and Security</h2>
        
        <h3>5.1 Data Storage</h3>
        <ul>
          <li>Your data is stored securely using Google Firebase services</li>
          <li>Data is encrypted in transit and at rest</li>
          <li>We implement industry-standard security measures</li>
        </ul>

        <h3>5.2 Data Retention</h3>
        <ul>
          <li>Account data: Retained while your account is active</li>
          <li>Tournament data: Retained for historical and statistical purposes</li>
          <li>Purchase history: Retained for accounting and customer service purposes</li>
          <li>Chat messages: Retained for moderation and support purposes</li>
          <li>Profile images: Stored in Firebase Storage, can be deleted by users</li>
          <li>Deck screenshots: Stored for tournament verification, can be removed after tournament completion</li>
        </ul>

        <h2>6. Your Rights and Choices</h2>
        
        <h3>6.1 Account Management</h3>
        <ul>
          <li>Update your profile information at any time</li>
          <li>Change your privacy settings</li>
          <li>Delete your account (contact us for assistance)</li>
        </ul>

        <h3>6.2 Data Access and Portability</h3>
        <ul>
          <li>Request a copy of your personal data</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your data (subject to legal requirements)</li>
        </ul>

        <h3>6.3 Communication Preferences</h3>
        <ul>
          <li>Opt out of promotional communications</li>
          <li>Manage notification preferences in app settings</li>
          <li>Unsubscribe from email communications</li>
        </ul>

        <h2>7. Third-Party Services</h2>
        
        <h3>7.1 Firebase (Google)</h3>
        <p>We use Google Firebase for authentication, database, storage, and cloud functions. Firebase handles user authentication securely and we do not store passwords directly. Firebase's privacy policy applies to their services.</p>

        <h3>7.2 Facebook/Messenger</h3>
        <p>If you choose to connect your Facebook account for tournament notifications, Facebook's privacy policy applies to their services. This is optional and you can use the app without connecting Facebook.</p>

        <h3>7.3 Payment Processors</h3>
        <p>Payment processing for DP (Duelist Points) purchases is handled by third-party services. We do not store payment card information or process payments directly.</p>

        <h3>7.4 Web Vitals</h3>
        <p>We use Google's Web Vitals library to measure app performance and user experience. This helps us improve the app's performance.</p>

        <h2>8. Children's Privacy</h2>
        <p>Our Service is designed to be family-friendly and can be used by children under 13 with parental supervision. We do not knowingly collect personal information from children under 13 without parental consent. If you are a parent or guardian and believe your child has provided us with personal information without your consent, please contact us immediately.</p>

        <h2>9. International Data Transfers</h2>
        <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.</p>

        <h2>10. Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any changes by:</p>
        <ul>
          <li>Posting the new Privacy Policy in the app</li>
          <li>Sending you an email notification</li>
          <li>Updating the "Last updated" date at the top of this policy</li>
        </ul>

        <h2>11. Contact Information</h2>
        <div className="contact-info">
          <p><strong>If you have any questions about this Privacy Policy or our data practices, please contact us:</strong></p>
          <ul>
            <li><strong>Email:</strong> jahamih@yahoo.com</li>
            <li><strong>Facebook Messenger:</strong> Contact us through our Facebook page</li>
            <li><strong>App Support:</strong> Use the in-app support feature</li>
          </ul>
        </div>

        <div className="highlight">
          <p><strong>Note:</strong> This Privacy Policy is effective as of the date listed above and applies to all users of the YME PAD application. By using our Service, you acknowledge that you have read and understood this Privacy Policy.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
